import { fetchWithAuth } from './auth';

export type FieldStatusValue = 'on_way' | 'arrived' | 'inspecting' | 'completed';
export type DriveDirection = 'outbound' | 'return' | 'between_jobs';

export interface FieldEventRecord {
  id: string;
  org_id: string;
  job_id: string;
  inspection_id: string | null;
  session_id: string | null;
  drive_session_id: string | null;
  user_id: string;
  event_type: string;
  event_source: string;
  occurred_at: string | null;
  metadata: Record<string, unknown>;
  created_at: string | null;
}

export interface FieldDriveSession {
  id: string;
  org_id: string;
  job_id: string;
  inspection_id: string | null;
  route_estimate_id: string | null;
  user_id: string;
  direction: DriveDirection;
  status: string;
  started_at: string | null;
  arrived_at: string | null;
  completed_at: string | null;
  origin_address: string | null;
  destination_address: string | null;
  route_distance_meters: number | null;
  route_duration_seconds: number | null;
  provider: string | null;
  metadata: Record<string, unknown>;
  created_at: string | null;
  updated_at: string | null;
}

export async function getLatestFieldStatus(jobId: string): Promise<{
  status: FieldStatusValue | null;
  event: FieldEventRecord | null;
}> {
  const r = await fetchWithAuth(`/api/field/events/status/${jobId}`);
  if (!r.ok) throw new Error(`getLatestFieldStatus failed: ${r.status}`);
  return (await r.json()) as { status: FieldStatusValue | null; event: FieldEventRecord | null };
}

export async function createFieldStatusEvent(params: {
  jobId: string;
  status: FieldStatusValue;
  inspectionId?: string | null;
  sessionId?: string | null;
  metadata?: Record<string, unknown>;
}): Promise<{ status: FieldStatusValue; event: FieldEventRecord }> {
  const r = await fetchWithAuth('/api/field/events/status', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      job_id: params.jobId,
      status: params.status,
      inspection_id: params.inspectionId ?? null,
      session_id: params.sessionId ?? null,
      metadata: params.metadata ?? {},
    }),
  });
  if (!r.ok) throw new Error(`createFieldStatusEvent failed: ${r.status}: ${(await r.text()).slice(0, 200)}`);
  return (await r.json()) as { status: FieldStatusValue; event: FieldEventRecord };
}

export async function startDriveSession(params: {
  jobId: string;
  inspectionId?: string | null;
  routeEstimateId?: string | null;
  direction?: DriveDirection;
  metadata?: Record<string, unknown>;
}): Promise<{ drive_session: FieldDriveSession; event: FieldEventRecord }> {
  const r = await fetchWithAuth('/api/field/drive-sessions/start', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      job_id: params.jobId,
      inspection_id: params.inspectionId ?? null,
      route_estimate_id: params.routeEstimateId ?? null,
      direction: params.direction ?? 'outbound',
      metadata: params.metadata ?? {},
    }),
  });
  if (!r.ok) throw new Error(`startDriveSession failed: ${r.status}: ${(await r.text()).slice(0, 200)}`);
  return (await r.json()) as { drive_session: FieldDriveSession; event: FieldEventRecord };
}
